import { makeStyles, tokens } from '@fluentui/react-components';

export const usePdmAssignmentStyles = makeStyles({
  pageRoot: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '12px',
  },

  // Tabs
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    paddingTop: '12px',
  },

  // Toolbar above the table — bottom border separates options from the table
  // (mirrors ProgramChangesPage `toolbar`).
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
    padding: '4px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '12px',
  },
  exportRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '12px',
  },
  filtersRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '8px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  resultsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: tokens.colorNeutralForeground3,
    padding: '8px 4px',
  },

  // Coverage
  banner: {
    padding: '6px 12px',
    marginBottom: '16px',
    flexShrink: 0,
  },
  bannerBody: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    columnGap: '6px',
    whiteSpace: 'normal',
  },
  bannerBreakdown: {
    color: tokens.colorNeutralForeground2,
    fontSize: '12px',
  },
  coveragePillButton: {
    display: 'inline-flex',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusCircular,
    ':hover': {
      opacity: 0.85,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '1px',
    },
  },

  // Table
  tableWrapper: {
    overflowX: 'auto',
    overflowY: 'auto',
    width: '100%',
    flex: 1,
    minHeight: '200px',
    scrollbarWidth: 'thin',
  },
  tierCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },
  partnerLink: {
    color: tokens.colorBrandForegroundLink,
    cursor: 'pointer',
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: tokens.colorNeutralForeground3,
  },
  messageBarWrapper: {
    marginBottom: '12px',
  },

  // Role assignment cell — PDM chips reuse the Market Categories pill style.
  roleCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '6px',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  roleCellInline: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  // Pill with an inline remove "×" — mirrors ProgramChangesPage countryChipEditable.
  pdmChip: {
    display: 'inline-flex',
    alignItems: 'stretch',
    height: '28px',
    padding: '0 0 0 12px',
    borderRadius: '9999px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  pdmChipLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingRight: '8px',
  },
  pdmChipRemove: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '100%',
    border: 'none',
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    background: 'transparent',
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    padding: 0,
    fontSize: '14px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorPaletteRedForeground1,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
    ':disabled': {
      cursor: 'not-allowed',
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  addAffordance: {
    color: tokens.colorBrandForegroundLink,
    cursor: 'pointer',
    fontSize: '13px',
    background: 'none',
    border: 'none',
    padding: 0,
    textAlign: 'left',
    ':hover': { textDecoration: 'underline' },
    ':disabled': {
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
      textDecoration: 'none',
    },
  },
  addAffordanceInline: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '28px',
    color: tokens.colorBrandForegroundLink,
    cursor: 'pointer',
    fontSize: '13px',
    background: 'none',
    border: 'none',
    padding: '0 4px',
    whiteSpace: 'nowrap',
    ':hover': { textDecoration: 'underline' },
    ':disabled': {
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
      textDecoration: 'none',
    },
  },

  // Add picker popover
  pickerSurface: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '280px',
    padding: '8px',
  },
  pickerList: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '220px',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
  },
  pickerGroupLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    padding: '4px 8px 2px',
  },
  pickerItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0px',
    padding: '6px 8px',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':disabled': { cursor: 'not-allowed', opacity: 0.6 },
  },
  pickerItemName: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
  pickerItemMeta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  pickerEmpty: {
    padding: '8px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  pickerError: {
    padding: '8px',
    fontSize: '12px',
    color: tokens.colorPaletteRedForeground1,
  },
  alreadyAssignedTag: {
    fontSize: '10px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  readOnlyEmpty: {
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  regionalHeader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  editRegionalLeadsButton: {
    minWidth: 'auto',
    height: '24px',
    padding: '0 6px',
  },
  regionalPanelBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '520px',
    maxWidth: '620px',
  },
  regionalPanelRow: {
    display: 'grid',
    gridTemplateColumns: '100px 1fr',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  regionalPanelRegion: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontWeight: 600,
    fontSize: '13px',
  },
  regionalPanelDot: {
    width: '10px',
    height: '10px',
    borderRadius: '9999px',
    flexShrink: 0,
  },
});
